/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IDobbiesFlipbookViewProps } from './dobbies-flipbook';

type FullscreenCapableElement = HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void;
    msRequestFullscreen?: () => Promise<void> | void;
};

type FullscreenCapableDocument = Document & {
    webkitFullscreenElement?: Element | null;
    msFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => Promise<void> | void;
    msExitFullscreen?: () => Promise<void> | void;
};

const PSEUDO_FULLSCREEN_BODY_CLASS = 'flipbook-module-pseudo-fullscreen-open';

export default (props: IDobbiesFlipbookViewProps) => {
    const {
        flipbookUrl,
        title,
        description,
        showTitle,
        showDescription,
        ctaText,
        showCta,
        desktopAspectRatio,
        mobileAspectRatio,
        maxWidth,
        backgroundColor,
        enableFullscreen
    } = props.config;

    const viewerRef = React.useRef<HTMLDivElement>(null);
    const [isNativeFullscreen, setIsNativeFullscreen] = React.useState(false);

    const [isPseudoFullscreen, setIsPseudoFullscreen] = React.useState(false);

    const isFullscreen = isNativeFullscreen || isPseudoFullscreen;

    React.useEffect(() => {
        const handleFullscreenChange = (): void => {
            const fullscreenDocument = document as FullscreenCapableDocument;
            const activeElement =
                fullscreenDocument.fullscreenElement ||
                fullscreenDocument.webkitFullscreenElement ||
                fullscreenDocument.msFullscreenElement;

            setIsNativeFullscreen(activeElement === viewerRef.current);
        };

        const handleVisibilityChange = (): void => {
            // Exit fullscreen if page becomes hidden (backgrounded on mobile)
            if (document.hidden) {
                setIsPseudoFullscreen(false);
                setIsNativeFullscreen(false);
            }
        };

        const handleOrientationChange = (): void => {
            // Reset fullscreen state when device orientation changes
            setIsPseudoFullscreen(false);
            setIsNativeFullscreen(false);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('orientationchange', handleOrientationChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('orientationchange', handleOrientationChange);
        };
    }, []);

    React.useEffect(() => {
        if (!isPseudoFullscreen) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape' || event.key === 'Esc') {
                event.preventDefault();
                setIsPseudoFullscreen(false);
            }
        };

        const handleTouchMove = (event: Event): void => {
            // Prevent scroll on touch devices
            event.preventDefault();
        };

        const scrollY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

        document.body.classList.add(PSEUDO_FULLSCREEN_BODY_CLASS);
        // Hold the pinned page at the offset it was read from, so closing the overlay does not
        // have to travel back up from the top of the document.
        document.body.style.top = `-${scrollY}px`;
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKeyDown);

        // Try adding touchmove with passive: false for mobile scroll prevention
        try {
            document.addEventListener('touchmove', handleTouchMove, { passive: false });
        } catch {
            // Fallback for browsers that don't support options
            document.addEventListener('touchmove', handleTouchMove);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('touchmove', handleTouchMove);

            const root = document.documentElement;

            // The theme sets `scroll-behavior: smooth` on the body, which Safari applies to the
            // viewport. Left in place it animates the jump below, so exiting the overlay reads as
            // the page scrolling down from the top of the document to the flipbook. Suppressed for
            // the length of the jump only, then handed back to the stylesheet.
            const previousRootScrollBehavior = root.style.scrollBehavior;
            const previousBodyScrollBehavior = document.body.style.scrollBehavior;
            root.style.scrollBehavior = 'auto';
            document.body.style.scrollBehavior = 'auto';

            // Unpin before scrolling: a fixed body cannot be scrolled.
            document.body.classList.remove(PSEUDO_FULLSCREEN_BODY_CLASS);
            document.body.style.top = '';
            document.body.style.overflow = '';
            root.style.overflow = '';

            // Synchronous, so no frame is ever painted at the top of the document.
            window.scrollTo(0, scrollY);

            root.style.scrollBehavior = previousRootScrollBehavior;
            document.body.style.scrollBehavior = previousBodyScrollBehavior;
        };
    }, [isPseudoFullscreen]);

    if (!flipbookUrl) {
        return null;
    }

    const style = {
        '--flipbook-desktop-ratio': desktopAspectRatio || '16 / 10',
        '--flipbook-mobile-ratio': mobileAspectRatio || '4 / 5',
        '--flipbook-max-width': `${maxWidth || 1400}px`,
        '--flipbook-background': backgroundColor || '#ffffff'
    } as React.CSSProperties;

    const enterFullscreen = async (): Promise<void> => {
        if (!enableFullscreen || !viewerRef.current) {
            return;
        }

        const element = viewerRef.current as FullscreenCapableElement;

        const request =
            typeof element.requestFullscreen === 'function'
                ? () => element.requestFullscreen()
                : typeof element.webkitRequestFullscreen === 'function'
                ? () => element.webkitRequestFullscreen!()
                : typeof element.msRequestFullscreen === 'function'
                ? () => element.msRequestFullscreen!()
                : undefined;

        if (!request) {
            // No Fullscreen API for generic elements (iPhone Safari) - use the overlay instead.
            setIsPseudoFullscreen(true);
            return;
        }

        try {
            await request();
        } catch (error) {
            console.warn('Unable to enter native fullscreen mode, falling back to overlay.', error);
            setIsPseudoFullscreen(true);
        }
    };

    const exitFullscreen = async (): Promise<void> => {
        setIsPseudoFullscreen(false);
        setIsNativeFullscreen(false);

        const fullscreenDocument = document as FullscreenCapableDocument;
        const activeElement =
            fullscreenDocument.fullscreenElement || fullscreenDocument.webkitFullscreenElement || fullscreenDocument.msFullscreenElement;

        if (!activeElement) {
            return;
        }

        const exit =
            typeof fullscreenDocument.exitFullscreen === 'function'
                ? () => fullscreenDocument.exitFullscreen()
                : typeof fullscreenDocument.webkitExitFullscreen === 'function'
                ? () => fullscreenDocument.webkitExitFullscreen!()
                : typeof fullscreenDocument.msExitFullscreen === 'function'
                ? () => fullscreenDocument.msExitFullscreen!()
                : undefined;

        try {
            if (exit) {
                await exit();
            }
        } catch (error) {
            console.error('Unable to exit fullscreen mode.', error);
        }
    };

    const handleCtaClick = async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
        event.preventDefault();
        await enterFullscreen();
    };

    return (
        <section className='flipbook-module' style={style} aria-labelledby={title ? 'flipbook-module-title' : undefined}>
            <div className='flipbook-module__container'>
                <div className='flipbook-module__content'>
                    {showTitle !== false && title && (
                        <h2 id='flipbook-module-title' className='flipbook-module__title'>
                            {title}
                        </h2>
                    )}

                    {showDescription !== false && description && <p className='flipbook-module__description'>{description}</p>}

                    {showCta !== false && enableFullscreen && ctaText && (
                        <button
                            type='button'
                            className='flipbook-module__cta'
                            onClick={handleCtaClick}
                            aria-label={`Open ${title || 'flipbook'} in fullscreen`}
                        >
                            {ctaText}
                        </button>
                    )}
                </div>

                <div className='flipbook-module__viewer-column'>
                    <div
                        ref={viewerRef}
                        className={`flipbook-module__viewer${isFullscreen ? ' flipbook-module__viewer--fullscreen' : ''}${
                            isPseudoFullscreen ? ' flipbook-module__viewer--pseudo-fullscreen' : ''
                        }`}
                        role='region'
                        aria-label={title || 'Flipbook viewer'}
                    >
                        {isFullscreen && (
                            <button
                                type='button'
                                onClick={exitFullscreen}
                                className={`flipbook-module__exit-fullscreen`}
                                aria-label='Exit fullscreen'
                            ></button>
                        )}
                        <iframe
                            className='flipbook-module__iframe'
                            src={flipbookUrl}
                            title={title || 'Flipbook viewer'}
                            allow='fullscreen'
                            allowFullScreen={enableFullscreen !== false}
                            referrerPolicy='strict-origin-when-cross-origin'
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};
